import { OverlapTransitionStateContext } from "@redotech/react-animation/outlet-transition";
import { OverlapTransitionState } from "@redotech/react-animation/transition";
import { useTranslation } from "@redotech/redo-i18n";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { useObservableState } from "observable-hooks";
import {
  createContext,
  Dispatch,
  Fragment,
  memo,
  type MouseEvent,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useMemo,
} from "react";
import { Link, useMatches } from "react-router-dom";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { map } from "rxjs/operators";
import * as breadcrumbCss from "./breadcrumb.module.css";
import { Flex } from "./flex";
import ChevronDown from "./icon-old/chevron-down.svg";
import { Text } from "./text";

export interface Breadcrumb {
  id: string;
  name: string;
  url: string | null;
  showLeadingBackIcon?: boolean;
  /** Runs before the link navigates; preventDefault to navigate another way. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export type BreadcrumbOverriders = Map<
  string,
  (breadcrumb: Breadcrumb) => Breadcrumb
>;
export const BreadcrumbOverridersContext = createContext<
  [BreadcrumbOverriders, Dispatch<SetStateAction<BreadcrumbOverriders>>]
>([new Map(), () => {}]);

const BREADCRUMB_DEFAULT = "";

export function breadcrumbSlot(): BreadcrumbSlot {
  return new BehaviorSubject(BREADCRUMB_DEFAULT);
}

export type BreadcrumbSlot = Subject<string>;

export function useBreadcrumb(
  breadcrumb: BreadcrumbSlot,
  value: string | undefined,
) {
  const transitionState = useContext(OverlapTransitionStateContext);
  const setValue = value || BREADCRUMB_DEFAULT;

  useLayoutEffect(() => {
    breadcrumb.next(setValue);

    // Skip cleanup during EXIT state to prevent overwriting the ENTER
    // component's breadcrumb value during OverlapTransition overlap window.
    if (transitionState === OverlapTransitionState.EXIT) {
      return undefined;
    }

    return () => breadcrumb.next(BREADCRUMB_DEFAULT);
  }, [breadcrumb, setValue, transitionState]);
}

export function useBreadcrumbs(): Breadcrumb[] | undefined {
  const { t } = useTranslation();
  const matches = useMatches();
  const transitionState = useContext(OverlapTransitionStateContext);
  const [breadcrumbOverriders] = useContext(BreadcrumbOverridersContext);
  const breadcrumbs = useMemo(() => {
    const observables: Observable<Breadcrumb>[] = [];
    for (const match of matches) {
      const handle: any = match.handle;
      if (handle?.breadcrumb === undefined) {
        continue;
      }
      const name: Observable<string> =
        typeof handle.breadcrumb === "string"
          ? of(handle.breadcrumb)
          : handle.breadcrumb;
      const url =
        handle.breadcrumbNavigate !== false
          ? match.pathname.replace(/\/$/, "")
          : null;
      const id = match.id;

      observables.push(
        name.pipe(
          map((name) => {
            let breadcrumb = { id, name, url };
            for (const overrider of breadcrumbOverriders?.values() ?? []) {
              breadcrumb = overrider(breadcrumb);
            }
            return breadcrumb;
          }),
        ),
      );
    }

    return combineLatest(observables);
    // `t` changes after locale switches and lazy resource loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, breadcrumbOverriders, t]);

  const observable = useObservableState(breadcrumbs);

  if (transitionState === OverlapTransitionState.EXIT) {
    return undefined;
  }

  return observable;
}

export type BreadcrumbSize = "xs" | "sm" | "lg";

export interface BreadcrumbsProps {
  size?: BreadcrumbSize;
  /**
   * List of breadcrumb names to filter out if they appear as the first breadcrumb.
   * Useful for hiding top-level navigation categories (e.g., "Home", "Order Management")
   * from the breadcrumb trail.
   */
  skipFirstIfMatches?: string[];
}

export type BreadcrumbWeight = "regular" | "semibold";

export interface BreadcrumbTrailProps {
  items: Breadcrumb[];
  size?: BreadcrumbSize;
  /** "regular" for a quiet trail, as on the return details header. */
  weight?: BreadcrumbWeight;
}

export const BreadcrumbTrail = memo(function BreadcrumbTrail({
  items,
  size = "sm",
  weight = "semibold",
}: BreadcrumbTrailProps) {
  const filteredItems = items.filter((b) => b.name !== "");

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <Flex align="center" gap="xs">
      {filteredItems.map((breadcrumb, i) => {
        const isParent = i < filteredItems.length - 1;
        const textColor = getBreadcrumbTextColor(size, isParent);
        const content = (
          <Flex
            align="center"
            gap="xs"
            hoverTextColor={breadcrumb.url ? "primary" : undefined}
            textColor={textColor}
          >
            {breadcrumb.showLeadingBackIcon && (
              <BreadcrumbBackIcon size={size} />
            )}
            <BreadcrumbText
              isLink={!!breadcrumb.url}
              isParent={isParent}
              size={size}
              text={breadcrumb.name}
              weight={weight}
            />
          </Flex>
        );

        return (
          <Fragment key={breadcrumb.id}>
            {0 < i && <BreadcrumbArrowIcon size={size} />}
            {breadcrumb.url ? (
              <Link
                onClick={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  (e.target as HTMLElement).blur();
                  breadcrumb.onClick?.(e);
                }}
                to={breadcrumb.url}
              >
                {content}
              </Link>
            ) : (
              content
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
});

export const Breadcrumbs = memo(function Breadcrumbs({
  size = "sm",
  skipFirstIfMatches = [],
}: BreadcrumbsProps) {
  const breadcrumbs = useBreadcrumbs();
  if (!breadcrumbs) {
    return null;
  }

  // Skip the first breadcrumb if it matches one of the filter values
  const first = breadcrumbs[0];
  const filteredBreadcrumbs =
    breadcrumbs.length > 1 &&
    skipFirstIfMatches.length > 0 &&
    first !== undefined &&
    skipFirstIfMatches.includes(first.name)
      ? breadcrumbs.slice(1)
      : breadcrumbs;

  return <BreadcrumbTrail items={filteredBreadcrumbs} size={size} />;
});

const BreadcrumbText = memo(function BreadcrumbText({
  text,
  isParent,
  isLink,
  size,
  weight = "semibold",
}: {
  text: string;
  isParent: boolean;
  isLink?: boolean;
  size: BreadcrumbSize;
  weight?: BreadcrumbWeight;
}) {
  // The xs variant is used in RedoPageHeader, where the current page is
  // rendered as the page title outside the trail. Every trail item is a
  // parent route, so they all share the muted treatment instead of
  // highlighting the last item.
  const textColor = getBreadcrumbTextColor(size, isParent);
  return (
    <Text
      className={breadcrumbCss.breadcrumb}
      fontSize={size}
      fontWeight={
        weight === "regular"
          ? "regular"
          : textColor === "placeholder"
            ? "medium"
            : "semibold"
      }
      hoverTextColor={isLink ? "primary" : undefined}
      textColor={textColor}
    >
      {text}
    </Text>
  );
});

const breadcrumbIconSize: Record<BreadcrumbSize, number> = {
  lg: 24,
  sm: 12,
  xs: 10,
};

const BreadcrumbBackIcon = memo(function BreadcrumbBackIcon({
  size,
}: {
  size: BreadcrumbSize;
}) {
  const iconSize = breadcrumbIconSize[size];
  return (
    <Flex
      align="center"
      data-testid="breadcrumb-back-icon"
      gap="none"
      shrink={0}
    >
      <ArrowLeftIcon height={iconSize} width={iconSize} />
    </Flex>
  );
});

const BreadcrumbArrowIcon = memo(function BreadcrumbArrowIcon({
  size,
}: {
  size: BreadcrumbSize;
}) {
  const iconSize = breadcrumbIconSize[size];
  return (
    <Flex align="center" gap="none" shrink={0} textColor="tertiary">
      <ChevronDown
        className={breadcrumbCss.breadcrumbArrow}
        height={iconSize}
        width={iconSize}
      />
    </Flex>
  );
});

function getBreadcrumbTextColor(
  size: BreadcrumbSize,
  isParent: boolean,
): "placeholder" | "primary" {
  return size === "xs" || isParent ? "placeholder" : "primary";
}
